import { useState } from "react";
import { Printer, X } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const fmt = (n) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const BUSINESS = {
  name: "CRIYE CELULARES",
  ig1: "@criyecelulares",
  ig2: "@criyecelulares2",
  ig3: "@mecanicoargentina",
  phone: "11 3158-2274",
  whatsapp: "https://wa.me/5491131582274",
};

const FORMATS = {
  "58mm": { width: "58mm", pad: "3mm", fontBase: "13px", fontTitle: "16px", lineHeight: 1.35, weight: 700 },
  "80mm": { width: "80mm", pad: "4mm", fontBase: "14px", fontTitle: "20px", lineHeight: 1.4, weight: 600 },
  a4: { width: "180mm", pad: "12mm", fontBase: "13px", fontTitle: "22px", lineHeight: 1.5, weight: 400 },
};

/**
 * TicketPrint — Modal con preview del ticket y selector de formato (58mm/80mm/A4).
 * Imprime usando window.print() — el usuario elige la impresora en el diálogo del navegador.
 *
 * Props:
 *   sale: { sale_id, items[], total, paid_amount, wallet_used, change_amount, payment_method, customer_name, seller_name, created_at }
 *   mode: "sale" (default) | "credit_note" — Nota de crédito (devolución/cancelación)
 *   creditNote: { items[], refund_total, reason, kind: "cancellation" | "return", new_wallet_balance } — solo si mode==="credit_note"
 *   onClose: () => void
 */
export default function TicketPrint({ sale, mode = "sale", creditNote = null, onClose }) {
  const [format, setFormat] = useState("58mm");
  const cfg = FORMATS[format];

  const handlePrint = () => {
    window.print();
  };

  if (!sale) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 print:hidden"
        data-testid="ticket-print-modal"
      >
        <div className="glass-card rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Printer size={20} weight="duotone" className="text-turquoise" />
              {mode === "credit_note" ? "Nota de crédito" : "Imprimir ticket"}
            </h3>
            <button onClick={onClose} data-testid="ticket-close-btn" aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>

          {/* Format selector */}
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
              Formato
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "58mm", label: "Térmica 58mm" },
                { key: "80mm", label: "Térmica 80mm" },
                { key: "a4", label: "Hoja A4" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setFormat(opt.key)}
                  data-testid={`ticket-format-${opt.key}`}
                  className={`py-2 px-2 text-[11px] uppercase tracking-wider font-mono rounded-md border transition ${
                    format === opt.key
                      ? "border-turquoise bg-turquoise/10 text-turquoise"
                      : "border-white/10 hover:border-white/20 text-zinc-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white text-black rounded-md p-3 mb-4 max-h-[400px] overflow-y-auto">
            <div
              id="ticket-preview"
              style={{
                fontFamily: "'Courier New', 'Lucida Console', monospace",
                fontSize: cfg.fontBase,
                fontWeight: cfg.weight,
                color: "#000",
                background: "#fff",
                padding: cfg.pad,
                width: format === "a4" ? "100%" : cfg.width,
                margin: "0 auto",
                lineHeight: cfg.lineHeight,
                WebkitFontSmoothing: "none",
                MozOsxFontSmoothing: "auto",
                fontSmooth: "never",
                textRendering: "geometricPrecision",
              }}
            >
              <TicketBody sale={sale} cfg={cfg} mode={mode} creditNote={creditNote} />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              data-testid="ticket-cancel-btn"
              className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5 transition"
            >
              Cerrar
            </button>
            <button
              onClick={handlePrint}
              data-testid="ticket-print-btn"
              className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <Printer size={16} weight="bold" /> Imprimir
            </button>
          </div>
        </div>
      </div>

      {/* Hidden printable area — only this prints */}
      <div className="print-only" aria-hidden>
        <div
          style={{
            fontFamily: "'Courier New', 'Lucida Console', monospace",
            fontSize: cfg.fontBase,
            fontWeight: cfg.weight,
            color: "#000",
            background: "#fff",
            padding: cfg.pad,
            width: cfg.width,
            margin: "0 auto",
            lineHeight: cfg.lineHeight,
            WebkitFontSmoothing: "none",
            fontSmooth: "never",
            textRendering: "geometricPrecision",
          }}
        >
          <TicketBody sale={sale} cfg={cfg} mode={mode} creditNote={creditNote} />
        </div>
      </div>

      <style>{`
        .print-only { display: none; }
        @media print {
          html, body { background: #fff !important; }
          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }
          .print-only {
            display: block !important;
            position: absolute;
            left: 0; top: 0;
            width: 100%;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-only * {
            color: #000 !important;
            font-weight: ${cfg.weight} !important;
            -webkit-font-smoothing: none !important;
            font-smooth: never !important;
            text-rendering: geometricPrecision !important;
          }
          @page {
            margin: 0;
            size: ${format === "a4" ? "A4" : `${cfg.width} auto`};
          }
        }
      `}</style>
    </>
  );
}

function Line() {
  return (
    <div
      style={{
        borderTop: "2px solid #000",
        margin: "5px 0",
        height: 0,
      }}
    />
  );
}

function TicketBody({ sale, cfg, mode = "sale", creditNote = null }) {
  const isCredit = mode === "credit_note" && creditNote;
  const items = isCredit ? (creditNote.items || []) : (sale.items || []);
  const headerTitle = isCredit ? "NOTA DE CRÉDITO" : BUSINESS.name;
  const subtitle = isCredit
    ? creditNote.kind === "cancellation"
      ? "Cancelación de venta"
      : "Devolución parcial"
    : "Comprobante no fiscal";

  // QR size adapts to format
  const qrSize = cfg.fontBase === "13px" ? 80 : cfg.fontBase === "14px" ? 110 : 130;

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "6px" }}>
        <div style={{ fontWeight: 900, fontSize: cfg.fontTitle, letterSpacing: "1.5px", color: "#000" }}>
          {headerTitle}
        </div>
        {isCredit && (
          <div style={{ fontWeight: 700, fontSize: "0.95em", marginTop: "2px" }}>
            {BUSINESS.name}
          </div>
        )}
        <div style={{ fontSize: "0.85em", marginTop: "2px", fontWeight: 600 }}>{subtitle}</div>
      </div>
      <Line />

      {/* Meta */}
      <div style={{ fontSize: "0.9em", lineHeight: 1.4 }}>
        <div>
          <strong>{isCredit ? "Nota:" : "Ticket:"}</strong>{" "}
          {String((isCredit && creditNote.return_id) || sale.sale_id || "").slice(-8).toUpperCase()}
        </div>
        {isCredit && (
          <div>
            <strong>Venta orig:</strong> {String(sale.sale_id || "").slice(-8).toUpperCase()}
          </div>
        )}
        <div>
          <strong>Fecha:</strong> {fmtDateTime((isCredit && creditNote.created_at) || sale.created_at)}
        </div>
        {sale.seller_name && (
          <div>
            <strong>{isCredit ? "Operador:" : "Vendedor:"}</strong>{" "}
            {isCredit ? (creditNote.operator_name || sale.seller_name) : sale.seller_name}
          </div>
        )}
        {sale.customer_name && (
          <div>
            <strong>Cliente:</strong> {sale.customer_name}
          </div>
        )}
        {isCredit && creditNote.reason && (
          <div>
            <strong>Motivo:</strong> {creditNote.reason}
          </div>
        )}
      </div>
      <Line />

      {/* Items */}
      <div style={{ fontSize: "0.9em" }}>
        {items.map((it, i) => (
          <div key={i} style={{ marginBottom: "4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ flex: 1, paddingRight: "4px", overflowWrap: "break-word" }}>
                {it.name}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>
                {it.qty} x ${fmt(isCredit ? it.unit_price : it.price)}
              </span>
              <span>${fmt(isCredit ? it.refund : it.subtotal)}</span>
            </div>
          </div>
        ))}
      </div>
      <Line />

      {/* Totals */}
      {isCredit ? (
        <div style={{ fontSize: "1em" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: "1.15em", padding: "3px 0" }}>
            <span>REEMBOLSO</span>
            <span>${fmt(creditNote.refund_total)}</span>
          </div>
          {creditNote.refunded_to_wallet ? (
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <span>Acreditado a billetera</span>
              <span>SÍ</span>
            </div>
          ) : (
            <div style={{ fontWeight: 700, fontSize: "0.9em", marginTop: "2px" }}>
              * Reembolso no aplicado a billetera (consumidor final)
            </div>
          )}
          {Number(creditNote.new_wallet_balance) >= 0 && creditNote.refunded_to_wallet && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Saldo billetera</span>
              <span>${fmt(creditNote.new_wallet_balance)}</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: "1em" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: "1.15em", padding: "3px 0" }}>
            <span>TOTAL</span>
            <span>${fmt(sale.total)}</span>
          </div>
          {sale.payment_method && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Método</span>
              <span style={{ textTransform: "capitalize" }}>{sale.payment_method}</span>
            </div>
          )}
          {Number(sale.wallet_used) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Billetera</span>
              <span>${fmt(sale.wallet_used)}</span>
            </div>
          )}
          {Number(sale.paid_amount) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Recibido</span>
              <span>${fmt(sale.paid_amount)}</span>
            </div>
          )}
          {Number(sale.change_amount) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Vuelto</span>
              <span>${fmt(sale.change_amount)}</span>
            </div>
          )}
        </div>
      )}
      <Line />

      {/* QR + Footer */}
      <div style={{ textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", margin: "4px 0 6px" }}>
          <QRCodeSVG
            value={BUSINESS.whatsapp}
            size={qrSize}
            level="M"
            bgColor="#FFFFFF"
            fgColor="#000000"
            includeMargin={false}
          />
        </div>
        <div style={{ fontSize: "0.8em", fontWeight: 700, marginBottom: "4px" }}>
          Escaneá para WhatsApp
        </div>
      </div>
      <div style={{ textAlign: "center", fontSize: "0.9em", lineHeight: 1.6, fontWeight: 600 }}>
        <div style={{ fontWeight: 900, marginBottom: "4px" }}>
          {isCredit ? "Conservar este comprobante" : "¡Gracias por su compra!"}
        </div>
        <div>IG: {BUSINESS.ig1}</div>
        <div>IG: {BUSINESS.ig2}</div>
        <div>IG: {BUSINESS.ig3}</div>
        <div style={{ marginTop: "4px", fontWeight: 700 }}>Tel: {BUSINESS.phone}</div>
      </div>
    </div>
  );
}
